import moment from 'moment';
import { DATE_FORMAT, DATE_FORMAT_WITH_TIME, SUPPORTED_IMAGE_FORMATS } from '../constants/common';
import { commonValidation } from './validations/validationRule';
import { BarInfo, TotalCombinedDataType } from '../types/common';
import { timezones } from '../constants/timezones';
import { ACCEPT_DECIMAL } from './apiDecimalConfig';
import { formatValue } from '@packages/display-policy';

// HTF-1482: company display precision (2 | 4 dp). Every money, decimal share quantity and
// percentage display says what the value is — `formatValue(x, 'money' | 'percent' | 'quantity',
// overrides?)` — and editable decimal inputs use `getInputText(raw, focused, kind, { hasError })`.
// The policy table in @packages/display-policy decides how each kind is shown.
// convertNumberWithCommas / ACCEPT_DECIMAL stay only for whole-number displays.
export { formatValue, getInputText } from '@packages/display-policy';
export type { DisplayKind } from '@packages/display-policy';

/**
 * HTF-1482: true when an input's value is still the text the user typed (identical, same text, or
 * numerically equal — a form may echo '12.50' as 12.5). Decimal inputs show typed text as typed
 * and format only values that came from outside (API).
 */
export const isSameInputValue = (a: unknown, b: unknown): boolean => {
  if (a === b || String(a ?? '') === String(b ?? '')) return true;
  if (a == null || a === '' || b == null || b === '') return false;
  return Number.isFinite(Number(a)) && Number(a) === Number(b);
};

type MoneyInputValue = number | string | null | undefined;
const isMissingMoney = (v: unknown): boolean => v === null || v === undefined || v === '';

/**
 * HTF-1482: product of two money/quantity inputs (e.g. grant value = qty * FMV) for formatValue(…, 'money').
 * undefined when either side is missing, so the cell is blank instead of a fake "$0.0000".
 */
export const moneyProduct = (a: unknown, b: unknown): number | undefined =>
  isMissingMoney(a) || isMissingMoney(b) ? undefined : Number(a) * Number(b);

/**
 * HTF-1482: "$low<sep>$high" money range. Both missing → ''; one missing → '-' for that side
 * (separator kept), e.g. "$12.5000/-", "- - $498.83". Callers keep their own ordering.
 */
export const formatMoneyRange = (first: unknown, second: unknown, separator: string): string => {
  const a = formatValue(first as MoneyInputValue, 'money');
  const b = formatValue(second as MoneyInputValue, 'money');
  if (a === '' && b === '') return '';
  return `${a === '' ? '-' : a}${separator}${b === '' ? '-' : b}`;
};

/**
 * HTF-1482 T5: footer text of a step-one quantity column total (sum of the typed quantities).
 * Float noise of the sum (0.1 + 0.2 = 0.30000000000000004, 0.7 + 0.1 = 0.7999999999999999) is
 * dropped for display only, then the total is shown per the setting like the "100% Qty" cell
 * (cut, whole stays whole). No positive total → '-'. Display only; the stored values are untouched.
 */
export const formatQuantityTotal = (total: unknown): string => {
  const n = Number(total);
  if (!(n > 0)) return '-';
  return (
    // N11a: legacy showed the raw sum (no grouping); only the decimals follow the setting.
    formatValue(Number(n.toPrecision(15)), 'quantity', {
      padding: 'fraction',
      grouping: false,
    }) || '-'
  );
};

/**
 * HTF-1482 C13: text of a step-one "100% Qty" table input (uncontrolled `defaultValue`) that
 * holds the prefilled full available quantity: per the setting when blurred (legacy
 * CommaFormatted parity: whole stays whole), the value as stored when focused. Display only.
 */
export const fullQtyInputText = (qty: unknown, focused: boolean): string => {
  if (qty === null || qty === undefined || qty === '') return '';
  if (focused) return String(qty);
  return (
    formatValue(qty as number | string, 'quantity', { padding: 'fraction', grouping: 'whole' }) ||
    String(qty)
  );
};

/**
 * HTF-1482 C13: swap an uncontrolled "100% Qty" input between the blurred / focused text while it
 * still shows the full quantity (call on focus, and on blur after the value has been read).
 * A typed value is never touched.
 */
export const swapFullQtyInputText = (
  input: HTMLInputElement | null | undefined,
  qty: unknown,
  focused: boolean
): void => {
  if (input == null) return;
  const from = fullQtyInputText(qty, !focused);
  if (from !== '' && input.value === from) input.value = fullQtyInputText(qty, focused);
};

export const phoneFormat = (name: string, setFieldValue: any, value: string): void => {
  if (value.length === 0) setFieldValue(name, value);
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) {
    setFieldValue(name, phoneNumber);
  } else if (phoneNumberLength < 7) {
    setFieldValue(name, `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`);
  } else {
    setFieldValue(
      name,
      `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
    );
  }
};

export const phoneFormatWithDash = (value: string | undefined): string => {
  const phoneNumber = value?.replace(/[^\d]/g, '');
  const formattedPhone =
    phoneNumber != null
      ? `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
      : '';
  return formattedPhone;
};

export const ssnFormatWithDash = (name: string, setFieldValue: any, value: string): void => {
  if (value.length === 0) setFieldValue(name, value);
  const ssnNumber = value.replace(/[^\d]/g, '');
  const ssnNumberLength = ssnNumber.length;
  if (ssnNumberLength < 4) {
    setFieldValue(name, ssnNumber);
  } else if (ssnNumberLength < 6) {
    setFieldValue(name, `${ssnNumber.slice(0, 3)}-${ssnNumber.slice(3)}`);
  } else {
    setFieldValue(
      name,
      `${ssnNumber.slice(0, 3)}-${ssnNumber.slice(3, 5)}-${ssnNumber.slice(5, 9)}`
    );
  }
};

export const ssnFormatWithDashNoFormik = (value?: string | null): string => {
  if (!value) return '';
  const ssnNumber = value.replace(/[^\d]/g, '');
  const ssnNumberLength = ssnNumber.length;
  if (ssnNumberLength < 4) {
    return ssnNumber;
  } else if (ssnNumberLength < 6) {
    return `${ssnNumber.slice(0, 3)}-${ssnNumber.slice(3)}`;
  } else {
    return `${ssnNumber.slice(0, 3)}-${ssnNumber.slice(3, 5)}-${ssnNumber.slice(5, 9)}`;
  }
};

export const changeArrayValues = (
  array: Record<string, string | number | boolean>[],
  rowId: string,
  name: string,
  value: string | number | boolean
): any => {
  const filter: any = array?.find(
    (item: any) => (item?.id || item?.awardId || item?.openLotId) === rowId
  );
  filter[`${name}`] = value;
  const existingIndex = array.findIndex(
    (item: any) => (item?.id || item?.awardId || item?.openLotId) === rowId
  );
  // If there is an overlap, replace the existing data with the new data
  array[existingIndex] = filter;
  return [...array];
};

export const ssnWithDash = (value: string | undefined | null): string => {
  const ssnNumber = value?.replace(/[^\d]/g, '');
  const formattedPhone = ssnNumber !== undefined ? `XXX-XX-${ssnNumber?.slice(5, 9)}` : '';
  return formattedPhone;
};

export const setDashAsEmptyValue = (values: any): any => values ?? '-';
export const checkEmpty = (values: string | number | boolean | any): boolean => {
  if (values !== undefined && values !== null) return true;
  else return false;
};

export const ssnDisplayFormat = (values: string, display: string): string => {
  const filteredValue = display.includes('show')
    ? `${values.slice(0, 3)}-${values.slice(3, 5)}-${values.slice(5, 9)}`
    : `XXX-XX-${values.slice(5, 9)}`;
  return filteredValue;
};

export const getNonUsCountryValidation = (
  isOtherCountry: boolean | undefined,
  fieldName: string
): any => {
  if (isOtherCountry ?? false) {
    return commonValidation({ required: true, noBlank: true }).matches(
      /^[a-zA-Z ]*$/,
      `Invalid ${fieldName} Name`
    );
  } else {
    return commonValidation({ noBlank: true }).matches(/^[a-zA-Z ]*$/, `Invalid ${fieldName} Name`);
  }
};

export const getUsCountryValidation = (isCountryUS: boolean | undefined): any => {
  if (!(isCountryUS ?? false)) {
    return commonValidation({ required: true });
  } else {
    return commonValidation({});
  }
};

export const getFileExtension = (name: string): any => {
  const fileArray = name?.split('.');
  const fileExtension = fileArray[fileArray?.length - 1];

  return fileExtension;
};

export const getFormData = (data: any): any => {
  const formData = new FormData(); // Must be FormData so that the ajax request will be Form post
  Object.keys(data).forEach((k) => {
    formData.append(k, data[k]);
  });
  return formData;
};

/**
 * Converts a JSON object to FormData, properly handling nested objects, arrays, and files (Blob)
 * @param data - The JSON object to convert
 * @param formData - Optional existing FormData instance
 * @param parentKey - Optional parent key for nested objects
 * @returns FormData instance
 */
export const jsonToFormData = (
  data: Record<string, any>,
  formData: FormData = new FormData(),
  parentKey: string = ''
): FormData => {
  if (data === null || data === undefined) {
    return formData;
  }

  Object.keys(data).forEach((key) => {
    const value = data[key];
    const formKey = parentKey ? `${parentKey}.${key}` : key;

    if (value === null || value === undefined) {
      // Skip null/undefined values
      return;
    }

    if (value instanceof File || value instanceof Blob) {
      // Handle File/Blob directly
      formData.append(formKey, value);
    } else if (Array.isArray(value)) {
      // Handle arrays
      value.forEach((item, index) => {
        const arrayKey = `${formKey}[${index}]`;
        if (item instanceof File || item instanceof Blob) {
          formData.append(arrayKey, item);
        } else if (typeof item === 'object' && item !== null) {
          // Recursively handle nested objects in array
          jsonToFormData(item, formData, arrayKey);
        } else {
          formData.append(arrayKey, String(item));
        }
      });
    } else if (typeof value === 'object' && !(value instanceof Date)) {
      // Recursively handle nested objects
      jsonToFormData(value, formData, formKey);
    } else if (value instanceof Date) {
      // Handle Date objects
      formData.append(formKey, value.toISOString());
    } else {
      // Handle primitive values
      formData.append(formKey, String(value));
    }
  });

  return formData;
};

export const handleOpenFile = (fileName: string, fileContent: string): void => {
  const fileExtension: string = getFileExtension(fileName);

  if (SUPPORTED_IMAGE_FORMATS.includes(fileExtension)) {
    const image = new Image();
    image.src = fileExtension != null ? `data:image/${fileExtension};base64,` + fileContent : '';
    const w: any = window.open('');
    w.document.write(image.outerHTML);
    w.document.close();
  } else if (fileExtension === 'pdf') {
    const pdfWindow: any = window.open('');
    pdfWindow.document.write(
      `<iframe width='100%' height='100%' src='data:application/pdf;base64, ` +
        encodeURI(fileContent) +
        "'></iframe>"
    );
  } else {
    const aElement = document.createElement('a');
    const downloadLink = `data:application/${fileExtension};base64,` + fileContent;
    aElement.setAttribute('download', fileName);
    aElement.setAttribute('href', downloadLink);
    aElement.click();
  }
};

export const handleDownloadFile = (fileName: string, fileContent: string): void => {
  const fileExtension: string = getFileExtension(fileName);

  const aElement = document.createElement('a');
  const downloadLink = `data:application/${fileExtension};base64,` + fileContent;
  aElement.setAttribute('download', fileName);
  aElement.setAttribute('href', downloadLink);
  aElement.click();
};
export const CommaFormatted = (
  amount: any,
  name?: string,
  setFieldValue?: any,
  ...rest: any
): string => {
  const numberValue = Number(String(amount)?.replaceAll(',', '') ?? '');

  // Check if the number has decimal places
  const hasDecimal = numberValue % 1 !== 0;
  const value = hasDecimal ? numberValue.toFixed(ACCEPT_DECIMAL) : numberValue.toLocaleString();

  if (name != null && Boolean(amount)) setFieldValue(name, !isNaN(numberValue) ? value : '');
  return value;
};

export const convertNumberWithCommas = (
  value?: number | string,
  showDecimal?: boolean,
  fixValue?: number
): string => {
  const NumberValue = Number(value)?.toFixed(ACCEPT_DECIMAL);
  value = String(value);
  const x: string[] = NumberValue?.toString().split('.');
  let x1 = x[0];
  const x2 = '.' + x[1];
  const rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    // eslint-disable-next-line no-useless-concat
    x1 = x1.replace(rgx, '$1' + ',' + '$2');
  }
  const finalValue = showDecimal ?? false ? x1 + x2 : x1;

  return finalValue;
};

export const CommaFormattedFloat = (amount: any, name?: string, setFieldValue?: any): any => {
  const numberValue = amount;
  const value =
    amount.toString().includes('.') === true
      ? numberValue
          .toFixed(ACCEPT_DECIMAL)
          .toLocaleString(undefined, { maximumFractionDigits: ACCEPT_DECIMAL })
      : numberValue.toLocaleString(undefined, { maximumFractionDigits: ACCEPT_DECIMAL });

  if (name != null && Boolean(amount)) setFieldValue?.(name, value);
  return value;
};

export const profitLossCalculation = (value?: number | string): string => {
  const calculatedValue = value?.toString().replaceAll('-', '');

  return Number(value) >= 0
    ? `+$${Number(calculatedValue)
        .toFixed(ACCEPT_DECIMAL)
        .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')}`
    : `-$${Number(Number(calculatedValue)?.toString())
        .toFixed(ACCEPT_DECIMAL)
        .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')}`;
};

export const changeTimeZone = (date: any, timeZone: string): any => {
  if (typeof date === 'string') {
    return moment(
      new Date(date)?.toLocaleString('en-US', {
        timeZone,
      })
    ).format(DATE_FORMAT_WITH_TIME);
  }

  return moment(
    date?.toLocaleString('en-US', {
      timeZone,
    })
  ).format(`${DATE_FORMAT} . h:mm A`);
};

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const handleStringCheck = (
  value: string,
  setCheck: ({
    longer,
    lowerCase,
    upperCase,
    isNumber,
    hasSymbol,
  }: {
    longer: boolean;
    lowerCase: boolean;
    upperCase: boolean;
    isNumber: boolean;
    hasSymbol: boolean;
  }) => void
): any => {
  const longer = value.length >= 8;
  const isNumber = /\d/.test(value);
  const hasSymbol = /[^A-Za-z0-9]/.test(value);
  const lowerCase = /[a-z]/.test(value);
  const upperCase = /[A-Z]/.test(value);

  setCheck({
    longer,
    lowerCase,
    upperCase,
    isNumber,
    hasSymbol,
  });
};

export const MaterialIconFilled = {
  fontVariationSettings: `"FILL" 1, "wght" 400, "GRAD" 0, "opsz" 48`,
};

export const combineDataByIndexValue = (info: BarInfo[]): TotalCombinedDataType[] => {
  const combinedData: any = {};

  info.forEach((item: any) => {
    const indexValue = item.data.indexValue;
    if (!combinedData[indexValue]) {
      combinedData[indexValue] = {
        width: item.width,
        x: item.x,
        y: item.y,
        indexValue: indexValue,
        totalValue: item.data.value,
      };
    } else {
      combinedData[indexValue].totalValue += item.data.value;
    }
  });

  return Object.values(combinedData);
};

export const getVestingSchedule = (
  sliderValue: number,
  latestPrice: number,
  exerciseCost: number,
  qty: number,
  isRSU: boolean
) => {
  let value: number = 0;
  if (isRSU) {
    value = Number(latestPrice) * qty;
  } else if (exerciseCost < latestPrice) {
    value = (latestPrice - exerciseCost) * qty;
  } else if (exerciseCost > latestPrice) {
    value = 0;
  }
  // Raw value; money is formatted at render (HTF-1482).
  return value;
};

export const getTzAbbr = function (dateInput?: any) {
  var dateObject = dateInput || new Date(),
    dateString = dateObject + '',
    tzAbbr =
      // Works for the majority of modern browsers
      dateString.match(/\(([^\)]+)\)$/) ||
      // IE outputs date strings in a different format:
      dateString.match(/([A-Z]+) [\d]{4}$/);

  if (tzAbbr) {
    // Old Firefox uses the long timezone name (e.g., "Central
    // Daylight Time" instead of "CDT")
    tzAbbr = tzAbbr[1].match(/[A-Z]/g)?.join('') as any;
  }

  // Uncomment these lines to return a GMT offset for browsers
  // that don't include the user's zone abbreviation (e.g.,
  // "GMT-0500".) I prefer to have `null` in this case, but
  // you may not!
  // First seen on: http://stackoverflow.com/a/12496442
  // if (!tzAbbr && /(GMT\W*\d{4})/.test(dateString)) {
  // 	return RegExp.$1;
  // }

  return tzAbbr;
};

export function getTimeZone() {
  const tzAbr = getTzAbbr();
  if (tzAbr) return tzAbr;
  const currentOffset = new Date().getTimezoneOffset();
  const offsetInHours = -currentOffset / 60;
  return timezones.find((tz) => tz.offset === offsetInHours)?.abbr;
}

export function convertToCurrentTimezone(inputDate: any) {
  let dateObj = inputDate;
  if (typeof inputDate === 'string') {
    dateObj = new Date(inputDate);
  }
  return moment(dateObj).format(`${DATE_FORMAT_WITH_TIME}`);
}

export const toCamelCase = (str: string): string => {
  return str
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
    .split(/\s+/) // Split by spaces
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
};

export const toFourDecimalPlaces = (value: number | string): string => {
  // Handle empty, null, undefined, or non-numeric values
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // Convert to number if it's a string
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if the conversion resulted in a valid number
  if (isNaN(numericValue)) {
    return '';
  }

  // Convert to 4 decimal places
  return numericValue.toFixed(ACCEPT_DECIMAL);
};
